import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  convertTo12HourFormat,
  handleDateSetUp,
} from "../../../../../helpers/helpers";
import DownloadPrototype from "./children/DownloadPrototype";
import InspectionHelpLine from "./components/InspectionHelpLine";
import DownloadPdfLoader from "../../../../loader/DownloadPdfLoader";
import { singleAuctionDetails } from "../../../../../redux/action/singleAuction";
import OfferTables from "../tables/OfferTables";

function DownloadInspectionPdf() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const contentRef = useRef();
  const headerRef = useRef();
  const footerRef = useRef();

  const { singleAuctionData, loading } = useSelector(
    (pre) => pre?.singleAuction
  );

  const [pdfGenerating, setPdfGenerating] = useState(false);

  useEffect(() => {
    dispatch(singleAuctionDetails(id));
  }, [id, dispatch]);

  const downloadPDF = async () => {
    const toastID = toast.loading("please wait...");
    setPdfGenerating(true);
    const content = contentRef.current;
    const header = headerRef.current;
    const footer = footerRef.current;

    try {
      const contentCanvas = await html2canvas(content, { scale: 1.5 });
      const headerCanvas = await html2canvas(header, { scale: 0.7 });
      const footerCanvas = await html2canvas(footer, { scale: 0.7 });

      const contentImgData = contentCanvas.toDataURL("image/png");
      const headerImgData = headerCanvas.toDataURL("image/png");
      const footerImgData = footerCanvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const marginLeft = 10;
      const marginRight = 10;
      const contentWidth = pageWidth - marginLeft - marginRight;

      const headerHeight =
        (headerCanvas.height * pageWidth) / headerCanvas.width;
      const footerHeight =
        (footerCanvas.height * pageWidth) / footerCanvas.width;
      const contentHeight =
        (contentCanvas.height * contentWidth) / contentCanvas.width;

      let currentPage = 1;
      let yPosition = 0;

      const addHeaderFooter = () => {
        pdf.addImage(headerImgData, "PNG", 0, 0, pageWidth, headerHeight);
        pdf.addImage(
          footerImgData,
          "PNG",
          0,
          pageHeight - footerHeight,
          pageWidth,
          footerHeight
        );
      };

      while (yPosition < contentHeight) {
        if (currentPage > 1) {
          pdf.addPage();
        }

        addHeaderFooter();

        const remainingSpace = pageHeight - headerHeight - footerHeight;
        const canvasChunk = Math.min(contentHeight - yPosition, remainingSpace);

        pdf.addImage(
          contentImgData,
          "PNG",
          marginLeft,
          headerHeight - yPosition,
          contentWidth,
          contentCanvas.height * (contentWidth / contentCanvas.width),
          "",
          "FAST"
        );

        yPosition += canvasChunk;
        currentPage++;
      }

      pdf.save(`inspection_details.pdf`);
      toast.update(toastID, {
        render: "File downloaded successfully",
        isLoading: false,
        autoClose: 5000,
        type: "success",
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setPdfGenerating(false);
    }
  };

  return (
    <>
      {loading ? (
        <DownloadPdfLoader />
      ) : (
        <>
          <div className="valign-wrapper space-between mb-1">
            <span className="valign-wrapper" style={{ gap: "25px" }}>
              <span
                className="material-icons-outlined pointer"
                onClick={() => navigate(-1)}
              >
                arrow_back
              </span>
              <h3>Inspection Details</h3>
            </span>
            <div
              className="valign-wrapper pointer download-btn cover"
              onClick={downloadPDF}
              style={{ padding: "5px 15px" }}
            >
              <span className="material-icons-outlined">file_download</span>
              <button disabled={pdfGenerating}>
                {pdfGenerating ? "Generating PDF..." : "Download PDF"}
              </button>
            </div>
          </div>
          <div className="white container pin-top">
            <div ref={headerRef} className="pdf-header">
              <div
                className={`cercle-purple valign-wrapper justify-center pdf-section`}
                style={{ borderRadius: "0px 0px 75px 75px" }}
              >
                <img
                  src="/images/auctionhomeLogo.png"
                  width="130px"
                  alt="logo-images"
                />
              </div>
            </div>
            <div className="container flex column gap-1 " ref={contentRef}>
              <h3 className="flex justify-center pdf-section">
                Inspection Request
              </h3>
              <div
                className="pdf-section"
                style={{
                  border: "2px solid #6f2da8",
                  borderCollapse: "collapse",
                }}
              >
                <h5
                  className="flex justify-center cercle-purple white-text font-cercular-bold margin-0px"
                  style={{ padding: "0.7rem" }}
                >
                  Seller Details
                </h5>
                <DownloadPrototype
                  label={"Auction ID"}
                  values={singleAuctionData?.auctionId}
                />
                <DownloadPrototype
                  label={"Auction Date & Time"}
                  values={`${convertTo12HourFormat(
                    singleAuctionData?.auctionSchedule?.startingTime
                  )} & ${handleDateSetUp(
                    singleAuctionData?.auctionSchedule?.startDate
                  )}`}
                />
                <DownloadPrototype
                  label={"Location"}
                  values={
                    singleAuctionData?.inspectionSchedule?.inspectionLocation
                  }
                />
                <DownloadPrototype
                  label={"Inspection Time"}
                  values={convertTo12HourFormat(
                    singleAuctionData?.inspectionSchedule?.startingTime
                  )}
                />
                <DownloadPrototype
                  label={"Description"}
                  values={singleAuctionData?.description}
                />
              </div>

              <div
                className=" pdf-section"
                style={{ border: "2px solid #6f2da8" }}
              >
                <h5
                  className="flex justify-center cercle-purple white-text font-cercular-bold margin-0px"
                  style={{ padding: "0.7rem" }}
                >
                  Buyer Details
                </h5>
                <DownloadPrototype
                  label={"Inspected by"}
                  values={singleAuctionData?.inspectionRequest?.inspectionBy}
                />
                <DownloadPrototype
                  label={"Inspection date"}
                  values={`${handleDateSetUp(
                    singleAuctionData?.inspectionRequest?.inspectionDate
                  )} `}
                />
                <DownloadPrototype
                  label={"Location"}
                  values={
                    singleAuctionData?.inspectionRequest?.inspectionLocation
                  }
                />
                <DownloadPrototype
                  label={"Number of people"}
                  values={singleAuctionData?.inspectionRequest?.numberOfPeople}
                />
                <DownloadPrototype
                  label={"Contact person name "}
                  values={singleAuctionData?.inspectionRequest?.contactPerson}
                />
              </div>

              <div className="pdf-section">
                <h3 className="flex justify-center">
                  List of Lots ( for visit )
                </h3>
                <OfferTables
                  loadings={loading}
                  OfferList={singleAuctionData?.offers}
                />
              </div>
            </div>
            <div
              className={`cercle-purple valign-wrapper space-between pdf-section`}
              style={{ borderRadius: "75px 75px 0px 0px" }}
              ref={footerRef}
            >
              <div className="container valign-wrapper space-between">
                <img
                  src="/images/auctionhomeLogo.png"
                  width="100px"
                  alt="logo-images"
                />
                <InspectionHelpLine />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default DownloadInspectionPdf;
